/**
 * Labstep
 *
 * @module components/Metadata/Form/ShowEdit/EntityRelation
 * @desc ShowEdit for EntityRelation
 */

import { EntityActionDelete } from 'labstep-web/components/Entity/Action/Delete';
import { useHasAccessCreate } from 'labstep-web/components/Entity/Can/hooks';
import { EntityLink } from 'labstep-web/components/Entity/Link';
import { EntityListCreate } from 'labstep-web/components/Entity/ListCreate';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import { UpdateOrCreate as UpdateOrCreateContainer } from 'labstep-web/containers/UpdateOrCreate';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { Hoverable } from 'labstep-web/core/Hoverable';
import type { Entity } from 'labstep-web/models/entity.model';
import { EntityRelation } from 'labstep-web/models/entity-relation.model';
import type { Resource } from 'labstep-web/models/resource.model';
import type React from 'react';
import type { MetadataFormShowEditEntityRelationProps } from './types';

export const MetadataFormShowEditEntityRelation: React.FC<
  MetadataFormShowEditEntityRelationProps
> = ({ metadata, entity, activeGroup }) => {
  if (
    !metadata.field_definition ||
    !activeGroup ||
    (entity as Resource).is_template
  ) {
    return null;
  }

  const createProps = metadata.getCreateProps(entity);
  const canCreate = entity.metadata_thread
    ? useHasAccessCreate(
        'metadata',
        entity.metadata_thread.entityName,
        entity.metadata_thread.id,
      )
    : false;

  // Use backend-provided preview if available (already limited & direction-aware)
  const entityRelations = metadata.entity_relation_preview || [];

  const disableActionButton =
    !metadata.field_definition.is_multiple_entity_relations_allowed &&
    entityRelations.length !== 0;

  // If metadata needs to be created first, wrap in UpdateOrCreateContainer
  if (createProps && canCreate) {
    return (
      <UpdateOrCreateContainer
        createProps={createProps}
        entityName={metadata.entityName}
        updateProps={{ id: metadata.id }}
      >
        {({ action: createMetadata }) => (
          <ActionComponent
            type="text"
            text="Show entity relations"
            onClick={() => createMetadata({} as any)}
          />
        )}
      </UpdateOrCreateContainer>
    );
  }

  return (
    <EntityListCreate
      disableAddButton={disableActionButton}
      group={activeGroup}
      entities={entityRelations}
      entityName={EntityRelation.entityName}
      entity={entity}
      searchEntityName={
        metadata.field_definition.entity_relation_target_entity_class
      }
      searchParams={
        metadata.field_definition.entity_relation_target_entity_filter
          ? {
              ...metadata.field_definition
                .entity_relation_target_entity_filter,
              group_id: activeGroup.id,
              count: 20,
            }
          : {
              group_id: activeGroup.id,
              count: 20,
            }
      }
      onChangeCreateProps={(selectedOption: Entity) => {
        return {
          field_definition_id: metadata.field_definition!.id,
          [`source_${metadata.field_definition!.entity_relation_target_entity_class}_id`]:
            entity.guid,
          [`target_${metadata.field_definition!.entity_relation_target_entity_class}_id`]:
            selectedOption.guid,
        };
      }}
      list={({ entities }) => {
        return (
          <Flex>
            {entities.map((entityRelation: EntityRelation) => {
              if (!entityRelation.target || !entityRelation.source) {
                return null;
              }
              const relationEntityLinked =
                entityRelation.source.guid === entity.guid
                  ? entityRelation.target
                  : entityRelation.source;
              return (
                <Flex grow>
                  <Hoverable
                    hoverable={
                      <EntityLink
                        key={relationEntityLinked.guid}
                        entity={relationEntityLinked}
                        showIcon
                      />
                    }
                    component={
                      <EntityActionDelete
                        entity={entityRelation}
                        actionComponentProps={{
                          type: 'icon',
                          icon: 'times',
                          elementProps: {
                            popup: { content: 'Remove' },
                          },
                        }}
                        skipConfirmation
                      />
                    }
                  />
                </Flex>
              );
            })}
          </Flex>
        );
      }}
    />
  );
};

export default withActiveGroup(MetadataFormShowEditEntityRelation);
